import React from 'react';
import { notification, IconType } from '@routific/hive';
import { UnscheduleIcon } from '@routific/hive/v5';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { useStore } from '../../../../store/mst';
import { unscheduleStopsFailedNotification } from '../../../../notifications/scheduling/unscheduleStop';
import ActionButton, { CommonActionTypes } from '../../../shared/ActionButton';
import { ActionButtonProps } from '../types';

const UnscheduleButton: React.FC<ActionButtonProps> = ({
  type,
  orders,
  buttonProps,
  hasPermission = false,
  v5,
  v5Hierarchy,
}) => {
  const store = useStore();
  const { mapView, model } = store;
  const { t } = useTranslation('components');

  const ordersProps = model.getOrdersProps(orders);

  const {
    disabled, isGeocoding, loading, hasUnscheduled, hasScheduled, hasCompleted, isUnscheduling, hasArrivedStop, uuids,
  } = ordersProps;

  const allUnscheduled = hasUnscheduled && !hasScheduled;

  if (!orders) {
    return null;
  }

  const getTooltipText = (): string => {
    switch (true) {
      case !hasPermission:
        return t('scheduleButton.tooltips.noPermission', { action: t('unscheduleButton.text.unschedule') });
      case isGeocoding:
        return t('multiSelectActionsBar.orders.tooltip.isGeocoding');
      case disabled:
        return t('unscheduleButton.tooltips.beingModified', { count: orders.length });
      case hasUnscheduled && orders.length === 1:
        return t('unscheduleButton.tooltips.alreadyUnscheduled');
      case allUnscheduled:
        return t('unscheduleButton.tooltips.alreadyUnscheduled_plural');
      case hasCompleted:
        return t('unscheduleButton.tooltips.scheduleCompleted', { count: orders.length });
      case hasArrivedStop:
        return t('unscheduleButton.tooltips.arrived', { count: orders.length });
      case isUnscheduling:
        return t('unscheduleButton.tooltips.unscheduling');
      default:
        return t('unscheduleButton.text.unschedule');
    }
  };

  const handleUnschedule = async () => {
    if (uuids && !loading) {
      try {
        await store.model.unscheduleStops(uuids);
      } catch (err) {
        notification(unscheduleStopsFailedNotification());
      } finally {
        mapView.setLassoMode(null);
        mapView.exitDrawMode();
      }
    }
  };


  return (
    <>
      <ActionButton
        name={CommonActionTypes.UNSCHEDULE}
        v5={v5}
        v5Hierarchy={v5Hierarchy}
        v5Icon={<UnscheduleIcon size="md" />}
        tooltipProps={{
          title: getTooltipText(),
        }}
        type={type}
        disabled={disabled || !hasPermission || allUnscheduled || hasCompleted || hasArrivedStop || isUnscheduling}
        loading={loading}
        menuItemText={t('unscheduleButton.text.unschedule')}
        menuItemProps={{
          onClick: handleUnschedule,
        }}
        buttonProps={{
          icon: IconType.Unschedule,
          onClick: handleUnschedule,
          ...buttonProps,
        }}
        textButtonProps={{
          title: t('unscheduleButton.text.unschedule'),
          onClick: handleUnschedule,
          type: 'link',
        }}
      />
    </>
  );
};

export default observer(UnscheduleButton);
